import { useRef, useCallback, useEffect } from "react";
import { useJoystickControls } from "ecctrl";

/**
 * Custom DOM-based mobile joystick that directly writes to ecctrl's
 * useJoystickControls zustand store.
 *
 * Replaces EcctrlJoystick which has a stale-closure bug: its onTouchMove
 * callback (cached by useCallback) captures `let` variables that are 0
 * on the render where the callback was created, causing joystickDis to
 * always clamp to 0 and producing NaN spring animation values.
 *
 * This component avoids the issue by reading dimensions via refs
 * (not closure-captured lets) and using plain DOM touch events.
 */
export default function MobileJoystick() {
  const baseRef = useRef(null);
  const handleRef = useRef(null);
  const setJoystick = useJoystickControls((s) => s.setJoystick);
  const resetJoystick = useJoystickControls((s) => s.resetJoystick);

  // Max distance the handle can travel from center (in px)
  const maxDis = 50;

  const onTouchStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.targetTouches[0];
      if (!touch || !baseRef.current) return;

      // Read fresh bounding rect every move — no stale closure
      const rect = baseRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = touch.clientX - centerX;
      const dy = -(touch.clientY - centerY); // invert Y for math coords

      const rawDis = Math.sqrt(dx * dx + dy * dy);
      const dis = Math.min(rawDis, maxDis);
      const ang = Math.atan2(dy, dx); // radians, 0 = right
      // Normalize to [0, 2π) to match ecctrl convention
      const normAng = ang < 0 ? ang + 2 * Math.PI : ang;

      const runState = dis > maxDis * 0.9;
      setJoystick(dis, normAng, runState);

      // Move the visual handle
      if (handleRef.current) {
        const clampedX = dis * Math.cos(ang);
        const clampedY = -dis * Math.sin(ang); // back to screen coords
        handleRef.current.style.transform = `translate(${clampedX}px, ${clampedY}px)`;
      }
    },
    [setJoystick, maxDis]
  );

  const onTouchEnd = useCallback(() => {
    resetJoystick();
    if (handleRef.current) {
      handleRef.current.style.transform = "translate(0px, 0px)";
    }
  }, [resetJoystick]);

  // Attach non-passive touch listeners so preventDefault works on iOS
  useEffect(() => {
    const el = baseRef.current;
    if (!el) return;
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  return (
    <div ref={baseRef} style={baseStyle}>
      <div ref={handleRef} style={handleStyle} />
    </div>
  );
}

export function MobileJumpButton() {
  const pressButton1 = useJoystickControls((s) => s.pressButton1);
  const releaseAllButtons = useJoystickControls((s) => s.releaseAllButtons);

  return (
    <div
      onPointerDown={() => pressButton1()}
      onPointerUp={() => releaseAllButtons()}
      onPointerCancel={() => releaseAllButtons()}
      onPointerLeave={() => releaseAllButtons()}
      style={buttonStyle}
    />
  );
}

/* ── Styles ───────────────────────────────────────────────── */

const baseStyle = {
  position: "fixed",
  left: 16,
  bottom: "12%",
  width: 130,
  height: 130,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.12)",
  border: "2px solid rgba(255,255,255,0.18)",
  zIndex: 9999,
  touchAction: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const handleStyle = {
  width: 50,
  height: 50,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.45)",
  pointerEvents: "none",
  transition: "transform 0.05s ease-out",
};

const buttonStyle = {
  position: "fixed",
  right: 24,
  bottom: "12%",
  width: 70,
  height: 70,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.2)",
  border: "2px solid rgba(255,255,255,0.25)",
  zIndex: 9999,
  touchAction: "none",
  cursor: "pointer",
};
